import { FC, Fragment, ReactNode, useCallback, useContext } from "react";
import { BannersContext, useBonusPagesConfig } from "@finbackoffice/site-core";
import { SiteContentScopes } from "@finbackoffice/enums";
import { BonusPagesEnum, IBonusPagesConfig } from "@finbackoffice/fe-core";
import Link from "next/link";
import Img from "components/base/img/Img";
import { RouteNames, RouterQuery } from "utils/constants";
import Translate from "components/base/translate/Translate";
import styles from "../bonuses.module.sass";

type PromotionLinkProps = {
    href: string;
    label: ReactNode;
    text: string;
    testId: string;
    src: string;
};

const PromotionLink: FC<PromotionLinkProps> = ({ href, label, text, testId, src }) => {
    return (
        <Link href={href} data-testid={testId} className={styles.promotionBanner} shallow>
            <>
                <span>
                    {src && (
                        <Img
                            source={src}
                            width={0}
                            height={0}
                            style={{ width: "auto", height: "100%" }}
                            alt=""
                        />
                    )}
                    <span className={styles.promotionBannerLabel}>{label}</span>
                </span>
                <span className={styles.promotionBannerTxt}>{text}</span>
                <i className={styles.promotionBannerIcon} />
            </>
        </Link>
    );
};

const BonusesPromotions: FC = () => {
    const enabledBonusesConfig = useBonusPagesConfig();
    const { banners } = useContext(BannersContext);

    const getBannerByScope = useCallback(
        (scope: SiteContentScopes) => {
            return banners.find((banner) => banner.tags.includes(scope));
        },
        [banners],
    );

    const getBannerData = useCallback(
        (scope: SiteContentScopes) => {
            const banner = getBannerByScope(scope);
            const bannerMedia = banner?.translations[0];
            const bannerSrc = bannerMedia?.bg_media?.url ?? "";
            const text = bannerMedia?.text || "";
            const label = bannerMedia?.title || "";

            return { bannerSrc, text, label };
        },
        [getBannerByScope],
    );

    const renderPromotions = useCallback(
        (item: IBonusPagesConfig) => {
            let content;

            switch (item.name) {
                case BonusPagesEnum.Referral: {
                    const { bannerSrc, text, label } = getBannerData(
                        SiteContentScopes.BonusReferral,
                    );

                    content = (
                        <PromotionLink
                            href={`/${RouteNames.BONUSES}/${RouteNames.BONUS_REFERRAL}`}
                            testId=""
                            src={bannerSrc}
                            label={label || <Translate tid="pageName_bonuses_referral" />}
                            text={text}
                        />
                    );
                    break;
                }
                case BonusPagesEnum.Cashback: {
                    const { bannerSrc, text, label } = getBannerData(
                        SiteContentScopes.BonusCashback,
                    );

                    content = (
                        <PromotionLink
                            href={`/${RouteNames.BONUSES}/${RouteNames.CASHBACK}`}
                            testId=""
                            src={bannerSrc}
                            label={label || <Translate tid="pageName_bonuses_cashback" />}
                            text={text}
                        />
                    );
                    break;
                }
                case BonusPagesEnum.Casino: {
                    const { bannerSrc, text, label } = getBannerData(
                        SiteContentScopes.BonusPromoCasino,
                    );

                    content = (
                        <PromotionLink
                            href={`/${RouteNames.BONUSES}/${RouteNames.PROMOTIONS}/${RouterQuery.Casino}`}
                            testId=""
                            src={bannerSrc}
                            label={label || <Translate tid="pageName_bonuses_promo_casino" />}
                            text={text}
                        />
                    );
                    break;
                }
                case BonusPagesEnum.Casino2: {
                    const { bannerSrc, text, label } = getBannerData(
                        SiteContentScopes.BonusPromoCasino2,
                    );

                    content = (
                        <PromotionLink
                            href={`/${RouteNames.BONUSES}/${RouteNames.PROMOTIONS}/${RouterQuery.Casino2}`}
                            testId=""
                            src={bannerSrc}
                            label={label || <Translate tid="pageName_bonuses_promo_casino_2" />}
                            text={text}
                        />
                    );
                    break;
                }
                case BonusPagesEnum.Promo2: {
                    const { bannerSrc, text, label } = getBannerData(SiteContentScopes.BonusPromo2);

                    content = (
                        <PromotionLink
                            href={`/${RouteNames.BONUSES}/${RouteNames.PROMOTIONS}/${RouterQuery.Promo2}`}
                            testId=""
                            src={bannerSrc}
                            label={label || <Translate tid="pageName_bonuses_promo_2" />}
                            text={text}
                        />
                    );
                    break;
                }
                case BonusPagesEnum.Promo3: {
                    const { bannerSrc, text, label } = getBannerData(SiteContentScopes.BonusPromo3);

                    content = (
                        <PromotionLink
                            href={`/${RouteNames.BONUSES}/${RouteNames.PROMOTIONS}/${RouterQuery.Promo3}`}
                            testId=""
                            src={bannerSrc}
                            label={label || <Translate tid="pageName_bonuses_promo_3" />}
                            text={text}
                        />
                    );
                    break;
                }
                case BonusPagesEnum.Promo4: {
                    const { bannerSrc, text, label } = getBannerData(SiteContentScopes.BonusPromo4);

                    content = (
                        <PromotionLink
                            href={`/${RouteNames.BONUSES}/${RouteNames.PROMOTIONS}/${RouterQuery.Promo4}`}
                            testId=""
                            src={bannerSrc}
                            label={label || <Translate tid="pageName_bonuses_promo_4" />}
                            text={text}
                        />
                    );
                    break;
                }
                case BonusPagesEnum.Promo5: {
                    const { bannerSrc, text, label } = getBannerData(SiteContentScopes.BonusPromo5);

                    content = (
                        <PromotionLink
                            href={`/${RouteNames.BONUSES}/${RouteNames.PROMOTIONS}/${RouterQuery.Promo5}`}
                            testId=""
                            src={bannerSrc}
                            label={label || <Translate tid="pageName_bonuses_promo_5" />}
                            text={text}
                        />
                    );
                    break;
                }
                case BonusPagesEnum.Promo6: {
                    const { bannerSrc, text, label } = getBannerData(SiteContentScopes.BonusPromo6);

                    content = (
                        <PromotionLink
                            href={`/${RouteNames.BONUSES}/${RouteNames.PROMOTIONS}/${RouterQuery.Promo6}`}
                            testId=""
                            src={bannerSrc}
                            label={label || <Translate tid="pageName_bonuses_promo_6" />}
                            text={text}
                        />
                    );
                    break;
                }
                case BonusPagesEnum.Aviator: {
                    const { bannerSrc, text, label } = getBannerData(
                        SiteContentScopes.BonusPromoAviator,
                    );

                    content = (
                        <PromotionLink
                            href={`/${RouteNames.BONUSES}/${RouteNames.PROMOTIONS}/${RouterQuery.Aviator}`}
                            testId=""
                            src={bannerSrc}
                            label={label || <Translate tid="pageName_bonuses_promo_aviator" />}
                            text={text}
                        />
                    );
                    break;
                }
                default:
                    content = null;
            }

            return item.enable ? <Fragment key={item.name}>{content}</Fragment> : null;
        },
        [getBannerData],
    );

    return (
        <section className={styles.navContent}>
            <div className={styles.promotions}>{enabledBonusesConfig.map(renderPromotions)}</div>
        </section>
    );
};

export default BonusesPromotions;
